import { useNavigate } from "react-router-dom";
import { Watch, Bot, Bell, Hospital, Play, Pause, Volume2, VolumeX } from "lucide-react";
import type { ReactNode } from "react";
import { useRef, useState, useEffect } from "react";

const s = {
  page:        { background: "#0d0d0f", color: "#f0ede8", minHeight: "100vh", fontFamily: "'Noto Sans Thai', Inter, sans-serif" } as React.CSSProperties,
  nav:         { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", borderBottom: "0.5px solid #2a2a2e", background: "#0d0d0f", position: "sticky" as const, top: 0, zIndex: 50 },
  logo:        { fontSize: 22, fontWeight: 600, color: "#f0ede8", letterSpacing: -0.5 },
  logoGreen:   { color: "#22c55e" },
  navLinks:    { display: "flex", gap: 28 },
  navLink:     { fontSize: 13, color: "#888", cursor: "pointer", textDecoration: "none", transition: "color 0.15s" } as React.CSSProperties,
  navCta:      { background: "#22c55e", color: "#fff", fontSize: 13, padding: "7px 20px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 500 } as React.CSSProperties,
  hero:        { padding: "44px 28px 32px", textAlign: "center" as const },
  badge:       { display: "inline-flex", alignItems: "center", gap: 6, background: "#1a1a1a", border: "0.5px solid #2a2a2e", borderRadius: 20, padding: "5px 14px", fontSize: 12, color: "#22c55e", marginBottom: 20 },
  dot:         { width: 7, height: 7, borderRadius: "50%", background: "#22c55e" },
  h1:          { fontSize: 38, fontWeight: 500, lineHeight: 1.2, marginBottom: 10 },
  h1em:        { color: "#22c55e", fontStyle: "normal" as const },
  heroSub:     { fontSize: 15, color: "#666", maxWidth: 440, margin: "0 auto 24px", lineHeight: 1.65 },
  heroBtns:    { display: "flex", justifyContent: "center", gap: 12 },
  btnPrimary:  { background: "#22c55e", color: "#fff", fontSize: 14, padding: "10px 26px", borderRadius: 22, border: "none", cursor: "pointer", fontWeight: 500 } as React.CSSProperties,
  btnGhost:    { background: "transparent", color: "#aaa", fontSize: 14, padding: "10px 22px", borderRadius: 22, border: "0.5px solid #333", cursor: "pointer" } as React.CSSProperties,
  statsBar:    { display: "flex", justifyContent: "center", gap: 48, padding: "18px 28px", borderTop: "0.5px solid #1e1e24", borderBottom: "0.5px solid #1e1e24", background: "#0a0a0c" },
  statVal:     { fontSize: 20, fontWeight: 500, color: "#f0ede8" },
  statLbl:     { fontSize: 11, color: "#555", marginTop: 2 },
  section:     { padding: "28px 28px" },
  secHeader:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  secTitle:    { fontSize: 16, fontWeight: 500, color: "#f0ede8" },
  navBtns:     { display: "flex", gap: 8 },
  navBtn:      { background: "#1a1a22", border: "0.5px solid #2a2a2e", color: "#888", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14 } as React.CSSProperties,
  grid4:       { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 },
  card:        { background: "#13131a", border: "0.5px solid #2a2a2e", borderRadius: 14, padding: "18px 14px", cursor: "pointer", display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 10 },
  cardActive:  { background: "#0e1f12", border: "1.5px solid #22c55e", borderRadius: 14, padding: "18px 14px", cursor: "pointer", display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 10 },
  logoCircle:  { width: 54, height: 54, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 },
  ticker:      { background: "#1e1e26", color: "#c8c4d8", fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 12 },
  cardName:    { fontSize: 11, color: "#555", letterSpacing: 0.3, textAlign: "center" as const },
  upGreen:     { fontSize: 12, fontWeight: 500, color: "#3cd48e" },
  downRed:     { fontSize: 12, fontWeight: 500, color: "#e8544a" },
  banner:      { margin: "0 28px", background: "linear-gradient(135deg,#0e1f12,#0a1a0e)", border: "0.5px solid #1e3a22", borderRadius: 14, padding: "22px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 },
  bannerH:     { fontSize: 17, fontWeight: 500, marginBottom: 5 },
  bannerSub:   { fontSize: 13, color: "#556", maxWidth: 260, lineHeight: 1.5 },
  bannerBtn:   { background: "#f0ede8", color: "#0d0d0f", fontSize: 13, padding: "10px 22px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" as const } as React.CSSProperties,
  miniLogos:   { display: "flex", gap: 8, marginLeft: 16, flexShrink: 0 },
  miniLogo:    { width: 38, height: 38, borderRadius: 9, background: "#13131a", border: "0.5px solid #2a2a2e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 } as React.CSSProperties,
  etfCard:     { background: "#13131a", border: "0.5px solid #2a2a2e", borderRadius: 14, padding: "14px 14px" },
  etfTop:      { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 },
  etfTicker:   { background: "#1e1e28", color: "#c8c4d8", fontSize: 12, fontWeight: 500, padding: "3px 8px", borderRadius: 8 },
  etfName:     { fontSize: 11, color: "#555", lineHeight: 1.4 },
  etfVal:      { fontSize: 15, fontWeight: 500, marginTop: 8, color: "#f0ede8" },
  footer:      { padding: "18px 28px", borderTop: "0.5px solid #1e1e24", display: "flex", justifyContent: "space-between", alignItems: "center" },
  footerTxt:   { fontSize: 12, color: "#444" },
};

type Feature = { icon: ReactNode; code: string; name: string; stat: string; up: boolean; active?: boolean };
const FEATURES: Feature[] = [
  { icon: <Watch  size={26} color="#9ca3af" />, code: "WATCH", name: "Smart Watch",  stat: "+98%",  up: true  },
  { icon: <Bot    size={26} color="#4ade80" />, code: "AI",    name: "Risk Scoring", stat: "+5.2%", up: true, active: true },
  { icon: <Bell   size={26} color="#9ca3af" />, code: "ALERT", name: "4-Level Alert",stat: "-0.3%", up: false },
  { icon: <Hospital size={26} color="#9ca3af" />, code: "DASH", name: "Dashboard",   stat: "+3.1%", up: true  },
];

const STATS = [
  { val: "4 ระดับ", lbl: "Escalation อัตโนมัติ" },
  { val: "<2 นาที", lbl: "เวลาตอบสนอง" },
  { val: "98%", lbl: "AI Accuracy" },
  { val: "24/7", lbl: "Realtime Monitor" },
];

const METRICS = [
  { code: "RISK", name: "AI Risk Assessment", val: "ใช้งานได้", change: "+พร้อม deploy", up: true, sparkUp: true },
  { code: "WATCH", name: "Watch Integration", val: "เชื่อมแล้ว", change: "+Apple Watch", up: true, sparkUp: true },
  { code: "ALERT", name: "Alert Engine",    val: "Active",    change: "+realtime",   up: true, sparkUp: true },
  { code: "API",   name: "REST API",        val: "FastAPI",   change: "−latency <50ms", up: false, sparkUp: false },
];

function Spark({ up }: { up: boolean }) {
  const pts = up
    ? "0,28 20,22 40,24 60,18 80,14 100,10 120,8"
    : "0,10 20,14 40,12 60,16 80,20 100,22 120,26";
  const fill = up ? "rgba(60,212,142,0.07)" : "rgba(232,84,74,0.07)";
  const stroke = up ? "#3cd48e" : "#e8544a";
  return (
    <svg style={{ width: "100%", height: 36, marginTop: 8 }} viewBox="0 0 120 36" fill="none">
      <polyline points={pts} stroke={stroke} strokeWidth="1.5" fill="none" />
      <path d={`M${pts.split(" ").map((p,i,a) => i===0?p:p).join(" ")} V36 H0Z`.replace("M","M")} fill={fill} />
    </svg>
  );
}

function MusicPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const update = () => setProgress((audio.currentTime / audio.duration) * 100 || 0);
    audio.addEventListener("timeupdate", update);
    return () => audio.removeEventListener("timeupdate", update);
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); } else { audio.play(); }
    setPlaying(!playing);
  };

  const toggleMute = () => {
    if (audioRef.current) audioRef.current.muted = !muted;
    setMuted(!muted);
  };

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 100,
      background: "#13131a", border: "0.5px solid #2a2a2e", borderRadius: 16,
      padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)", minWidth: 220 }}>
      <audio ref={audioRef} src="/tuendee-theme.mp3" loop />
      <button onClick={toggle} style={{ width: 36, height: 36, borderRadius: "50%",
        background: playing ? "#22c55e" : "#1a2e1a", border: "1px solid #22c55e44",
        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 } as React.CSSProperties}>
        {playing ? <Pause size={16} color="#fff" /> : <Play size={16} color="#22c55e" />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: "#f0ede8", fontWeight: 500, marginBottom: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          เตือนดี — ดูแลด้วยใจ
        </div>
        <div style={{ height: 3, background: "#2a2a2e", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#22c55e", borderRadius: 2, transition: "width 0.5s linear" }} />
        </div>
      </div>
      <button onClick={toggleMute} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0 } as React.CSSProperties}>
        {muted ? <VolumeX size={14} color="#555" /> : <Volume2 size={14} color="#666" />}
      </button>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={s.page}>

      {/* Nav */}
      <nav style={s.nav}>
        <div style={{ ...s.logo, display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/tuendee_logo.png" alt="เตือนดี" style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover" }} />
          <span style={s.logoGreen}>เตือนดี</span>
          <span style={{ color: "#555", fontSize: 15 }}>TuenDee</span>
        </div>
        <div style={s.navLinks}>
          {[["ฟีเจอร์","features"],["วิธีการทำงาน","how"],["เทคโนโลยี","metrics"],["ติดต่อ","footer"]].map(([l,id]) => (
            <a key={l} href={`#${id}`} style={s.navLink}
              onMouseEnter={e => (e.currentTarget.style.color="#f0ede8")}
              onMouseLeave={e => (e.currentTarget.style.color="#888")}
            >{l}</a>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => navigate("/login")} style={{ ...s.btnGhost, padding: "7px 16px" }}>
            เข้าสู่ระบบ
          </button>
          <button style={s.navCta} onClick={() => navigate("/login")}>ลองเดโม่</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.badge}>
          <div style={s.dot} />
          ระบบดูแลผู้ป่วยเรียลไทม์ สำหรับ รพสต. คลินิก และโรงพยาบาล
        </div>
        <h1 style={s.h1}>
          เตือนตรงเวลา<br />
          <em style={s.h1em}>ดูแลได้ทันที</em>
        </h1>
        <p style={s.heroSub}>
          แพลตฟอร์มเตือนสุขภาพอัจฉริยะ เชื่อมผู้ป่วย–ผู้ดูแล–โรงพยาบาล
          ด้วยระบบแจ้งเตือน 4 ระดับและ AI วิเคราะห์ความเสี่ยงรายบุคคล
        </p>
        <div style={s.heroBtns}>
          <button style={s.btnPrimary}>นัดสาธิตระบบ →</button>
          <button style={s.btnGhost} onClick={() => navigate("/login")}>Dashboard ตัวอย่าง</button>
        </div>
      </section>

      {/* Stats bar */}
      <div style={s.statsBar}>
        {STATS.map(st => (
          <div key={st.lbl} style={{ textAlign: "center" }}>
            <div style={s.statVal}>{st.val}</div>
            <div style={s.statLbl}>{st.lbl}</div>
          </div>
        ))}
      </div>

      {/* Feature cards — like "Top Traded Stocks" */}
      <section id="features" style={s.section}>
        <div style={s.secHeader}>
          <div style={s.secTitle}>ฟีเจอร์หลัก</div>
          <div style={s.navBtns}>
            <div style={s.navBtn}>‹</div>
            <div style={s.navBtn}>›</div>
          </div>
        </div>
        <div style={s.grid4}>
          {FEATURES.map(f => (
            <div key={f.code} style={f.active ? s.cardActive : s.card}>
              <div style={{ ...s.logoCircle, background: f.active ? "#1a2e1a" : "#1a1a22" }}>
                {f.icon}
              </div>
              <div style={f.active
                ? { ...s.ticker, background: "#1a2e1a", color: "#4ade80" }
                : s.ticker
              }>{f.code}</div>
              <div style={s.cardName}>{f.name}</div>
              <div style={f.up ? s.upGreen : s.downRed}>{f.stat}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Banner — like "Activate Daily Free Predictions" */}
      <div style={s.banner}>
        <div>
          <div style={s.bannerH}>เปิดใช้งาน TuenDee สำหรับโรงพยาบาลของคุณ</div>
          <p style={s.bannerSub}>
            รับ AI Risk Score และแจ้งเตือนผู้ป่วยทุกรายแบบเรียลไทม์ พร้อม deploy ทันที
          </p>
          <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
            <button style={s.bannerBtn}>ติดต่อทีมงาน</button>
            <span style={{ fontSize: 12, color: "#445" }}>ไม่มีค่าใช้จ่ายเริ่มต้น</span>
          </div>
        </div>
        <div style={s.miniLogos}>
          <div style={s.miniLogo}><Watch    size={18} color="#9ca3af" /></div>
          <div style={s.miniLogo}><Bot      size={18} color="#4ade80" /></div>
          <div style={s.miniLogo}><Hospital size={18} color="#9ca3af" /></div>
        </div>
      </div>

      {/* ฟีเจอร์ */}
      <section id="how" style={{ ...s.section, padding: "40px 28px" }}>
        <div style={{ ...s.secHeader, marginBottom: 28 }}>
          <div style={s.secTitle}>วิธีการทำงาน</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, position: "relative" }}>
          {[
            { step: "01", title: "ตั้งเวลายา", desc: "ระบบหรือเจ้าหน้าที่กำหนดตารางยาและ threshold สัญญาณชีพรายบุคคล", icon: "💊" },
            { step: "02", title: "เตือนผู้ป่วย", desc: "นาฬิกา Apple Watch และมือถือรับแจ้งเตือนตรงเวลา ยืนยันการทาน", icon: "⌚" },
            { step: "03", title: "แจ้งผู้ดูแล", desc: "ถ้าไม่ตอบสนองใน 10 นาที ระบบ escalate ไปผู้ดูแลและ รพสต. อัตโนมัติ", icon: "👨‍⚕️" },
            { step: "04", title: "ส่งต่อฉุกเฉิน", desc: "กรณีวิกฤต AI ประเมิน risk score และเรียกรถฉุกเฉิน พร้อมส่งข้อมูลล่วงหน้า", icon: "🚑" },
          ].map((item, i) => (
            <div key={item.step} style={{ position: "relative" }}>
              {i < 3 && (
                <div style={{ position: "absolute", right: 0, top: "36px", width: "100%", height: 1, background: "linear-gradient(90deg,#22c55e44,#22c55e22)", zIndex: 0, transform: "translateX(50%)" }} />
              )}
              <div style={{ background: "#13131a", border: "0.5px solid #2a2a2e", borderRadius: 14, padding: "24px 20px", margin: "0 8px", position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: i === 3 ? "#1a0e0e" : "#0e1f12", border: `1px solid ${i === 3 ? "#c0392b44" : "#22c55e44"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                    {item.icon}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: i === 3 ? "#e8544a" : "#22c55e", letterSpacing: 1 }}>STEP {item.step}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 500, color: "#f0ede8", marginBottom: 8 }}>{item.title}</div>
                <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Metric cards — like "Top Traded Index ETFs" */}
      <section id="metrics" style={s.section}>
        <div style={s.secHeader}>
          <div style={s.secTitle}>สถานะระบบ</div>
          <div style={s.navBtns}>
            <div style={s.navBtn}>‹</div>
            <div style={s.navBtn}>›</div>
          </div>
        </div>
        <div style={s.grid4}>
          {METRICS.map(m => (
            <div key={m.code} style={s.etfCard}>
              <div style={s.etfTop}>
                <div style={s.etfTicker}>{m.code}</div>
                <div style={m.up ? s.upGreen : s.downRed}>{m.up ? "●" : "●"}</div>
              </div>
              <div style={s.etfName}>{m.name}</div>
              <div style={s.etfVal}>{m.val}</div>
              <div style={{ fontSize: 11, color: m.up ? "#3cd48e" : "#e8544a", marginTop: 4 }}>
                {m.change}
              </div>
              <Spark up={m.sparkUp} />
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="footer" style={{ padding: "40px 28px 20px", borderTop: "0.5px solid #1e1e24" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 40 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <img src="/tuendee_logo.png" alt="เตือนดี" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover" }} />
              <span style={{ fontSize: 16, fontWeight: 600, color: "#22c55e" }}>เตือนดี</span>
            </div>
            <p style={{ fontSize: 12, color: "#555", lineHeight: 1.8 }}>
              แพลตฟอร์มดูแลสุขภาพผู้ป่วยเรียลไทม์<br />
              สำหรับ รพสต. คลินิก และโรงพยาบาล<br />
              พัฒนาโดย Harvest Hub Corp.
            </p>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#444", letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" as const }}>ลิงก์ด่วน</div>
            {[["ฟีเจอร์หลัก","#features"],["วิธีการทำงาน","#how"],["เทคโนโลยี","#metrics"],["เข้าสู่ระบบ","/login"]].map(([l,href]) => (
              <a key={l} href={href} style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 8, textDecoration: "none", cursor: "pointer" } as React.CSSProperties}
                onMouseEnter={e => (e.currentTarget.style.color="#f0ede8")}
                onMouseLeave={e => (e.currentTarget.style.color="#555")}
              >{l}</a>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#444", letterSpacing: 1, marginBottom: 14, textTransform: "uppercase" as const }}>ติดต่อ</div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>thanawin@harvesthubs.app</div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>harvesthubs.app</div>
            <div style={{ marginTop: 16 }}>
              <button
                onClick={() => window.location.href = "mailto:thanawin@harvesthubs.app"}
                style={{ background: "#13131a", border: "0.5px solid #2a2a2e", color: "#f0ede8", fontSize: 13, padding: "9px 20px", borderRadius: 20, cursor: "pointer" } as React.CSSProperties}
              >
                ส่งอีเมลหาเรา
              </button>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "0.5px solid #1e1e24", paddingTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={s.footerTxt}>© 2026 TuenDee · Harvest Hub Corp.</div>
          <div style={{ fontSize: 12, color: "#333" }}>tuendee.harvesthubs.app</div>
        </div>
      </section>

      <MusicPlayer />
    </div>
  );
}
